import { Component, OnInit, ViewContainerRef, ViewChild, ComponentRef, Input } from '@angular/core';
import { WallboardViewService } from '../wallboard-view.service';
import { WVMain } from '../wallboard-view.model';
import { CvConfigurationService } from 'src/app/reusables/services/cv-configuration.service';
import { WallboardViewErrorComponent } from '../wallboard-view-error/wallboard-view-error.component';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';

@Component({
    selector: 'cv-wallboard-view-main',
    templateUrl: './wallboard-view-main.component.html',
    styleUrls: ['./wallboard-view-main.component.scss']
})
export class WallboardViewMainComponent implements OnInit {
    @ViewChild('main', { read: ViewContainerRef }) viewContainerRef: ViewContainerRef;
    @ViewChild('preRender', { read: ViewContainerRef }) nextSlideRef: ViewContainerRef;
    
    // do not use directly - use from config service.isCxOne instead
    @Input()
    set isCxone(value: string) {
        this.cvConfigurationService.isCxOne = value === "true";
    }
    public wvf: WVMain;
    public isLoading: boolean = true;
    public hasError: boolean = false;
    public errorMessage: string = '';
    private reloadPageTime;
    private retryCounter = 0; // Counter to attempt to load next slide
    private componentRef: ComponentRef<any> = null;
    private nextComponentRef: ComponentRef<any> = null;
    private readonly LOCALIZATION_MESSAGES = `Wallboard_Management_Messages`;
    public localizationMessages: Map<string, string>;

    constructor(
        private service: WallboardViewService,
        private cvLocalizationFactory: CVLocalizationService,
        private cvConfigurationService: CvConfigurationService,
    ) { }

    ngOnInit(): void {
        this.wvf = this.service.WVMain;
        this.initializeWallboard();
    }

    private async initializeWallboard(): Promise<void> {
        try {
            this.localizationMessages = await this.cvLocalizationFactory.GetSection(this.LOCALIZATION_MESSAGES);
            await this.service.LoadWallboardInfoForView();
            this.loadSlide();
        }
        catch (error) {
            if (error.message === 'WALLBOARD_INACTIVE') {
                this.hasError = true;
                this.errorMessage = this.localizationMessages.get('Wallboard_unavailable');
                this.isLoading = false;
                this.showErrorComponent();
            } else {
                console.error('Error loading wallboard:', error);
                this.hasError = true;
                this.errorMessage = this.localizationMessages.get('Loading_Error');
                this.isLoading = false;
                this.showErrorComponent();
            }
        }
    }

    async resetWallboardInfo() {
        try {
            const currentSlide = this.wvf.CurrentSlide;
            this.wvf.LastSlide = this.wvf.Slides[currentSlide]
            await this.service.LoadWallboardInfoForView();  // Request the current wallboard settings
            this.wvf.NextSlide = 0; // Set the nextSlide to load as the first one of the batch
            this.wvf.CurrentSlide = currentSlide; // Reseting the CurrentSlide to wvf
        }
        catch (error) {
            if (error.message === 'WALLBOARD_INACTIVE') {
                this.hasError = true;
                this.errorMessage = this.localizationMessages.get('Wallboard_unavailable');
                this.showErrorComponent();
            } else {
                console.error('Error resetting wallboard info:', error);
            }
        }
    }

    // slideIndex of -1 loads all slides, a value >=0 will load only the slide at that order position

    shouldForceReload() {
        if (this.reloadPageTime === null)
            return false;
        else
            return Date.now() > this.reloadPageTime;
    }

    delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms))
    }

    async prepareNextSlide() {
        const slideInterval = this.wvf.Slides[this.wvf.CurrentSlide].SlideInterval;
        const delayTime = slideInterval > 15 ? Math.floor((slideInterval * 1000) / 2) : 5000;

        await this.delay(delayTime);

        // Halfway through the slide interval (or 5 secs on a min slide interval) start pre-load of next slide.
        if (this.shouldForceReload())
            return; // TransitionSlides() will do the actual reload.  But don't pre-load the next slide.

        if (this.wvf.CurrentSlide == this.wvf.Slides.length - 1 && this.wvf.NextSlide == 0) // Check that the currentSlide is the last slide of the stack and that the nextSlide to load is the first slide
            await this.resetWallboardInfo()

        this.wvf.NextSlideReady = false;
        this.RenderNextSlide()
    }

    async loadSlide() {
        if (this.wvf.Slides.length > 0 && this.wvf.FirstSlideReady) {
            this.isLoading = false; // Hide loader once first slide is ready
            const slideInterval = this.wvf.Slides[this.wvf.CurrentSlide].SlideInterval;

            // Start both preparation and main timing concurrently
            const preparePromise = this.prepareNextSlide();
            const slideDelayPromise = this.delay(slideInterval * 1000);

            // Wait for both to complete
            await Promise.all([preparePromise, slideDelayPromise]);

            this.transitionSlides();
        }
        else if (this.wvf.Slides.length > 0 && this.wvf.FirstSlideReady == false) {
            if (!this.componentRef)
                this.RenderFirstSlide();

            await this.delay(1000)

            this.loadSlide();
        }
        else {
            await this.delay(1000)

            this.loadSlide();
        }
    }

    transitionSlides() {
        if (this.shouldForceReload()) {
            // Amazon Fire TV Sticks have a memory leak and will stop running through the slide show.  Forcing a reload is a workaround.
            window.location.reload();
            return;
        }

        if (this.wvf.NextSlideReady) {
            // If next slide is ready, rotate slides in factory, remove old slide from DOM, tell new slide to run.

            // Can we run the animations here to avoid the double desynced animations?
            this.RunAnimations();

            this.service.rotateSlides();
            setTimeout(() => {
                setTimeout(() => {
                    this.wvf.RunSlide = true;
                    this.SwitchContainers();
                    this.retryCounter = 0; // It worked! Reset the retryCounter
                    this.loadSlide();
                }, 1000); // Trying a wait of 1000 from 0 to allow animations time to run
            });
        }
        else if (this.wvf.SkipNextSlide) {
            // If next slide failed to load, remove slide from DOM, skip it in factory, and load the next slide.
            this.service.skipSlide();
            this.wvf.SkipNextSlide = false;
            this.retryCounter = 0;
            setTimeout(() => {
                setTimeout(() => {
                    this.wvf.NextSlideReady = false;
                    this.RenderNextSlide();
                }, 0);
            });
            setTimeout(() => {
                this.transitionSlides();
                this.retryCounter++;
            }, 1000);
        }
        else if (this.retryCounter >= 60) {
            this.wvf.SkipNextSlide = true;
            this.transitionSlides();
        }
        else {
            setTimeout(() => {
                this.transitionSlides();
                this.retryCounter++;
            }, 1000);
        }
    };

    RenderFirstSlide() {
        this.componentRef = this.viewContainerRef.createComponent(this.service.loadSlide());
        this.componentRef.instance.firstSlide = true;
        this.componentRef.instance.isCxone = this.cvConfigurationService.isCxOne;
    }

    RenderNextSlide() {
        this.nextSlideRef.clear();
        this.nextComponentRef = this.nextSlideRef.createComponent(this.service.loadSlide(true));
        this.nextComponentRef.instance.firstSlide = false;
        this.componentRef.instance.isCxone = this.cvConfigurationService.isCxOne;
        this.service.loadNextSlideData();
    }

    SwitchContainers() {
        this.viewContainerRef.remove(0);
        this.componentRef = this.nextComponentRef;
        this.viewContainerRef.insert(this.nextComponentRef.hostView);
        this.nextComponentRef = null;
    }

    RunAnimations() {
        const currentSlide = this.wvf.LastSlide ?? this.wvf.Slides[this.wvf.CurrentSlide]
        let moduleKey = currentSlide.WallboardModuleDetails?.[0]?.WallboardModuleKey || ''

        if (this.wvf.LastSlide)
            this.wvf.LastSlide = null;

        switch (currentSlide.TransitionID) {
            case 0: // None
            case 1: // Blinds
            case 2: // Checkerboard
            case 3: // Dissolve
                break;
            case 4: // Fade
                $(".wallboardViewSlide[mid=" + moduleKey + "]:first").fadeOut(1000);
                break;
            case 5: // Flip
                $(".wallboardViewSlide[mid=" + moduleKey + "]:first").css({ "-webkit-animation": "flip 1s", "animation": "flip 1s" });
                break;
            case 6: // Pin Hole
                $(".wallboardViewSlide[mid=" + moduleKey + "]:first").css({ "-webkit-animation": "pinhole 1s", "animation": "pinhole 1s" });
                break;
            case 7: // Shatter
                break;
            case 8: // Slide Bottom
                $(".wallboardViewSlide[mid=" + moduleKey + "]:first").css("top", "-100vh");
                break;
            case 9: // Slide Left
                $(".wallboardViewSlide[mid=" + moduleKey + "]:first").css("left", "100vw");
                break;
            case 10: // Slide Right
                $(".wallboardViewSlide[mid=" + moduleKey + "]:first").css("left", "-100vw");
                break;
            case 11: // Slide Top
                $(".wallboardViewSlide[mid=" + moduleKey + "]:first").css("top", "100vh");
                break;
            default:
                $(".wallboardViewSlide[mid=" + moduleKey + "]:first").css("left", "100vw");
                break;
        }
    }

    showErrorComponent() {
        this.viewContainerRef.clear();
        const errorComponentRef = this.viewContainerRef.createComponent(WallboardViewErrorComponent);
        errorComponentRef.instance.errorMessage = this.errorMessage;
    }
}
